import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMatches, usePredictions } from '../../contexts/AppContext';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorMessage from '../common/ErrorMessage';
import TimezoneIndicator from '../common/TimezoneIndicator';
import { formatKickoffTime, formatShortDate } from '../../utils/dateUtils';
import { useI18n } from '../../i18n';

const UpcomingMatches = () => {
  const { t } = useI18n();
  const { fixtures, fetchFixtures, loading, error } = useMatches();
  const { userPredictions } = usePredictions();
  const [upcomingMatches, setUpcomingMatches] = useState([]);

  useEffect(() => {
    // Fetch upcoming matches for next 7 days (starting from now, not from today)
    const now = new Date();
    const nextWeek = new Date(now);
    nextWeek.setDate(now.getDate() + 7);

    fetchFixtures({
      from: now.toISOString(),
      to: nextWeek.toISOString(),
      status: 'NOT_STARTED'
    });
  }, [fetchFixtures]);

  useEffect(() => {
    if (fixtures?.length) {
      // Filter for truly upcoming matches (future dates + NOT_STARTED status)
      const now = new Date();
      const upcomingOnly = fixtures.filter(match => {
        // Parse match date as UTC to avoid timezone issues
        const matchDate = new Date(match.date + 'Z'); // Force UTC interpretation
        return matchDate > now && match.status === 'NOT_STARTED';
      });
      
      // Sort by date and take first 5
      const sorted = upcomingOnly
        .sort((a, b) => new Date(a.date + 'Z') - new Date(b.date + 'Z'))
        .slice(0, 5);
      setUpcomingMatches(sorted);
    }
  }, [fixtures]);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <div className="divide-y divide-gray-200 dark:divide-gray-700">
      {/* Timezone indicator header */}
      <div className="p-4 bg-gray-50 dark:bg-gray-700 border-b dark:border-gray-600">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('predictions.upcomingMatches')}</h3>
          <TimezoneIndicator showDetails={false} />
        </div>
      </div>

      {upcomingMatches.map((match) => {
        const prediction = userPredictions.find(p => p.fixture_id === match.fixture_id);

        return (
          <Link
            key={match.fixture_id}
            to={prediction 
              ? `/predictions/${prediction.id}` 
              : `/predictions/new?match=${match.fixture_id}`}
            className="block hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <div className="p-4">
              {/* Date and Time - FIXED: Using timezone utilities */}
              <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                {formatKickoffTime(match.date)}
              </div>

              {/* Teams and Prediction Status */}
              <div className="grid grid-cols-7 items-center gap-2">
                {/* Home Team */}
                <div className="col-span-3 flex items-center space-x-2">
                  {match.home_team_logo ? (
                    <img
                      src={match.home_team_logo}
                      alt={`${match.home_team} logo`}
                      className="h-6 w-6 object-contain"
                      onError={(e) => { 
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className="h-6 w-6 bg-gray-200 dark:bg-gray-600 rounded flex items-center justify-center" style={{ display: match.home_team_logo ? 'none' : 'flex' }}>
                    <span className="text-gray-500 dark:text-gray-400 text-xs">⚽</span>
                  </div>
                  <span className="font-medium truncate text-gray-900 dark:text-gray-100">{match.home_team}</span>
                </div>

                {/* VS or Prediction */}
                <div className="col-span-1 text-center">
                  {prediction ? (
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {prediction.score1}-{prediction.score2}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-500 dark:text-gray-400">{t('matches.vs')}</span>
                  )}
                </div>

                {/* Away Team */}
                <div className="col-span-3 flex items-center justify-end space-x-2">
                  <span className="font-medium truncate text-gray-900 dark:text-gray-100">{match.away_team}</span>
                  {match.away_team_logo ? (
                    <img
                      src={match.away_team_logo}
                      alt={`${match.away_team} logo`}
                      className="h-6 w-6 object-contain"
                      onError={(e) => { 
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className="h-6 w-6 bg-gray-200 dark:bg-gray-600 rounded flex items-center justify-center" style={{ display: match.away_team_logo ? 'none' : 'flex' }}>
                    <span className="text-gray-500 dark:text-gray-400 text-xs">⚽</span>
                  </div>
                </div>
              </div>

              {/* League and Prediction Status */}
              <div className="mt-2 flex justify-between items-center text-sm">
                <span className="text-gray-500 dark:text-gray-400">{match.league}</span>
                {prediction ? (
                  <span className="px-2 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-full text-xs">
                    {t('matches.predictionMade')}
                  </span>
                ) : (
                  <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 rounded-full text-xs">
                    {t('matches.predictNow')}
                  </span>
                )}
              </div>
            </div>
          </Link>
        );
      })}

      {/* Show message if no upcoming matches */}
      {upcomingMatches.length === 0 && (
        <div className="p-6 text-center text-gray-500 dark:text-gray-400">
          <p className="mb-2">{t('predictions.noUpcomingMatches')}</p>
          <p className="text-sm text-gray-400 dark:text-gray-500">{t('matches.checkBackLater')}</p>
        </div>
      )}

      {/* Link to all matches */}
      <div className="p-4 bg-gray-50 dark:bg-gray-700">
        <Link
          to="/predictions"
          className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm font-medium"
        >
          {t('matches.viewAllUpcoming')} →
        </Link>
      </div>
    </div>
  );
};

export default UpcomingMatches;